import { ControlEventTypes } from "Core/Events/EventTypes";
import { DropDownService, TextBlockService } from "Service";
import { IDataRow, IODataResult } from "Entities";
import { Navigation } from "Core";
import { BaseDropDownControl } from "Controls/BaseDropDownControl";
import { TextBlockCategory } from "Enums";
import { ControlDefinition, DataRequestOption, Environment } from "Service/Client";

/**
 * Control for selecting values from a dropdown list.
 * The list is searchable with text input.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a ComboBox with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class ComboBox extends BaseDropDownControl {
    /**
     * The css class which should be unique for each control type.
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-combobox";

    /**
     * @hidden
     */
    protected bindingProperty: string = this.controlDefinition.LoadDataAsynchronously ? "deferredValueAsync" : "deferredValue";

    /**
     * @hidden
     */
    protected additionalBinding =
        this.controlDefinition.TextColumnName && this.controlDefinition.ValueColumnName != this.controlDefinition.TextColumnName
            ? "listText: " + this.controlDefinition.TextColumnName
            : "";

    private _kendoElement: kendo.ui.ComboBox;

    private data: IODataResult;
    private valueIndex: number = null; // stored index for initial value selection

    private dataSourceOptions: kendo.data.DataSourceOptions;
    private valueBeforeBinding: string;

    private comboboxService: DropDownService;

    /**
     * Create a new ComboBoxControl.
     * @hidden
     */
    constructor(
        controlDefinition: ControlDefinition,
        textBlockCategory: string,
        textBlockService: TextBlockService,
        environment: Environment,
        comboBoxService: DropDownService
    ) {
        super(controlDefinition, textBlockCategory, textBlockService, environment);
        this.comboboxService = comboBoxService;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));

        let dropDownOptions = this.getDropDownOptions();

        let noDataTemplate = dropDownOptions.noDataTemplate;
        if (this.controlDefinition.Link) {
            let noDataAddText = this.textBlockService
                .get("ComboboxNoDataAddText", TextBlockCategory.global)
                .split("@@comboboxText@@")
                .join("#: instance.text() #");
            noDataTemplate += `<div class="dms-combobox-addtext">${noDataAddText}`;
            noDataTemplate += `<br/><button class="k-button k-button-icontext dms-combobox-addbutton"><span class="far fa-plus"></span>&nbsp;${this.textBlockService.get(
                "ComboboxNoDataAddButton",
                TextBlockCategory.global
            )}</button></div>`;
        }

        let options: kendo.ui.ComboBoxOptions = {
            dataValueField: this.controlDefinition.CommandValueColumnName,
            dataTextField: this.controlDefinition.CommandTextColumnName,
            valuePrimitive: true, // sets the binding to the value field instead of the dataitem itself
            suggest: false,
            filter: "contains",
            autoBind: false,
            change: (e) => this.onChange(e),
            // select: (e) => this.onSelect(e),
            dataBound: (e) => this.onDataBound(e),
            noDataTemplate: noDataTemplate,
            autoWidth: this.controlDefinition.AutoWidth || false,
            footerTemplate: (data: any) => {
                let combobox = <kendo.ui.ComboBox>data.instance;
                let itemCount = combobox.dataSource.total();
                // if null value was added by the server
                if (this.controlDefinition.InsertNullValue) {
                    if (this.controlDefinition.LoadDataAsynchronously) {
                        if ((<any>combobox)._state != "filter" || combobox.text() == "") {
                            itemCount--;
                        }
                    } else {
                        let view = combobox.dataSource.view();
                        // check if the null value is in the result.
                        // if true, we need to reduce the shown count by 1 because the null value should not count as item
                        if (view.length > 0 && view[0][this.controlDefinition.CommandValueColumnName] == null) {
                            itemCount--;
                        }
                    }
                }
                return `<div class="dms-dropdown-footer k-widget k-header">${itemCount} ${this.textBlockService.get(
                    "ComboboxItemsLabel",
                    TextBlockCategory.global
                )}</div>`;
            },
            syncValueAndText: false,
            clearButton: this.controlDefinition.InsertNullValue || false,
            template: dropDownOptions.template,
            headerTemplate: dropDownOptions.headerTemplate,
        };

        if (this.controlDefinition.Caption) {
            options.placeholder = this.textBlockService.get(this.controlDefinition.Caption, this.textBlockCategory);
        }

        if (this.controlDefinition.LoadDataAsynchronously) {
            this.dataSourceOptions = {
                transport: {
                    read: (options: kendo.data.DataSourceTransportReadOptions) => {
                        // check if loaded data contains the requested page
                        if (this.data && this.data.value) {
                            let value = this.data.value;
                            if (value[options.data.skip]) {
                                let data = value[options.data.skip];
                                delete value[options.data.skip];
                                this.valueBeforeBinding = this._kendoElement.value();

                                options.success({
                                    value: data,
                                    "odata.count": this.data["odata.count"],
                                });
                                return;
                            }
                        }

                        let dataRow = this.execute<IDataRow, {}>(ControlEventTypes.GetDataRow);
                        let requestOptions = this.getDataRequest();
                        requestOptions.SearchValue = null;
                        if (options.data.filter && options.data.filter.filters.length > 0) {
                            let item = <kendo.data.DataSourceFilterItem>options.data.filter.filters[0];
                            requestOptions.SearchText = item.value;
                        }
                        requestOptions.Skip = options.data.skip;

                        this.comboboxService
                            .get(this.controlDefinition.GridID, this.controlDefinition.ControlID, requestOptions, dataRow)
                            .then((result: IODataResult) => {
                                if (result.error) {
                                    Navigation.errorAsync(result.error, "Error");
                                    return;
                                }

                                options.success({
                                    value: result.value[0],
                                    "odata.count": result["odata.count"],
                                });
                            });
                    },
                },
                schema: {
                    data: function (data: any) {
                        return data["value"];
                    },
                    total: function (data: any) {
                        return data["odata.count"];
                    },
                },
                error: (e) => this.onError(e),
                serverPaging: true,
                serverFiltering: true,
                pageSize: this.controlDefinition.ItemsPerRequest > 0 ? this.controlDefinition.ItemsPerRequest : 10,
            };

            let asyncOptions: kendo.ui.ComboBoxOptions = {
                height: ((this.controlDefinition.ItemsPerRequest > 0 ? this.controlDefinition.ItemsPerRequest : 10) / 4) * 30,
                virtual: {
                    itemHeight: 30,
                    valueMapper: (options: any) => {
                        if (this.valueIndex !== undefined) {
                            options.success(this.valueIndex == -1 ? 0 : this.valueIndex);
                            this.valueIndex = undefined;
                            return;
                        }

                        if (!options.value) {
                            options.success(0); // try to select the first value
                            return;
                        }

                        this.data = null;
                        let dataRow = this.execute<IDataRow, {}>(ControlEventTypes.GetDataRow);
                        let requestOptions: DataRequestOption = {
                            ControlID: this.controlDefinition.ControlID,
                            SearchValue: options.value != "" ? [options.value] : [],
                        };
                        this.comboboxService
                            .mapValue(this.controlDefinition.GridID, this.controlDefinition.ControlID, requestOptions, dataRow)
                            .then((result) => {
                                if (result.Error) {
                                    Navigation.errorAsync(result.Error, "Error");
                                    return;
                                }

                                options.success(result.selectedIndex.length > 0 ? result.selectedIndex[0] : 0);
                                if (result.selectedIndex.length == 0) {
                                    this._kendoElement.value(null);
                                    this._kendoElement.trigger("change");
                                }
                            });
                    },
                },
            };

            $.extend(options, asyncOptions);
        } else {
            this.dataSourceOptions = {
                transport: {
                    read: (options: kendo.data.DataSourceTransportRead) => {
                        if (this.data != null) {
                            this.valueBeforeBinding = this._kendoElement.value();
                            (options.success as JQuery.Ajax.SuccessCallback<any>)(this.data, null, null);
                            return;
                        }
                    },
                },
                schema: {
                    data: function (data: IODataResult) {
                        return data.value[0];
                    },
                    total: function (data: IODataResult) {
                        return data["odata.count"];
                    },
                },
                error: (e) => this.onError(e),
            };
        }

        this._kendoElement = new kendo.ui.ComboBox(this.jQueryElement[0], options);
        this._kendoElement.popup.element.addClass("dms-control-dropdown-popup");

        if (!this.controlDefinition.LoadDataAsynchronously) {
            (<any>this._kendoElement)._filterSource = (s: any) => {
                if (!s) {
                    this._kendoElement.dataSource.filter({});
                } else {
                    let filter: kendo.data.DataSourceFilters = {};
                    filter.logic = "or";
                    filter.filters = [];

                    if (this.controlDefinition.DataColumns && this.controlDefinition.DataColumns.length > 0) {
                        for (let i = 0, len = this.controlDefinition.DataColumns.length; i < len; i++) {
                            let dataColumn = this.controlDefinition.DataColumns[i];
                            filter.filters.push({
                                field: dataColumn.ColumnName,
                                operator: "contains",
                                value: s.value,
                            });
                        }
                    } else {
                        filter.filters.push({
                            field: this._kendoElement.options.dataTextField,
                            operator: "contains",
                            value: s.value,
                        });
                    }
                    this._kendoElement.dataSource.filter(filter);
                }
            };
        }

        if (dropDownOptions.setListWidth) {
            let listWidth = dropDownOptions.listWidth;
            if (listWidth > this._kendoElement.element.width()) {
                this._kendoElement.popup.element.width(listWidth);
            }
        }

        this._kendoElement.list.addClass("dms-control-combobox-list");

        if (this.controlDefinition.Link) {
            this._kendoElement.list.on("click", ".dms-combobox-addbutton", (e) => {
                this.onAddButtonClick();
            });
        }
    }

    private onAddButtonClick() {
        let link = this.resolve(this.controlDefinition.Link);
        link = link.split("@@comboboxText@@").join(this._kendoElement.text());
        Navigation.openWindow(link, this.controlDefinition.LinkMode);
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Format the value with highlighting from text search.
     * @param value Value to be formatted.
     * @hidden
     */
    public formatValue(value: string): string {
        let text = this._kendoElement.text();
        let idx = this._kendoElement.select();
        if (!text || !value || idx == -1) {
            return value;
        }
        if (this.lastText != text) {
            // todo: refactor replace to own service.
            this.textMatcher = new RegExp(text.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"), "ig");
            this.lastText = text;
        }
        return value.toString().replace(this.textMatcher, function (match) {
            return "<em>" + match + "</em>";
        });
    }

    /**
     * Occurs after data was bound on the control.
     * @param e EventArgs.
     */
    private onDataBound(e: kendo.ui.ComboBoxDataBoundEvent): void {
        this.setBusy(false);

        if (this.controlDefinition.LoadDataAsynchronously) {
            // if the value was changed during binding, trigger "change" for dependent controls.
            if (this.valueBeforeBinding !== undefined && this._kendoElement.value() != this.valueBeforeBinding) {
                this._kendoElement.trigger("change");
                this.valueBeforeBinding = undefined;
            }
            this.setDataPromiseResolve();
        } else {
            // if no element is selected, try to select the first element.
            if (this.valueBeforeBinding !== undefined && this._kendoElement.select() == -1) {
                // if the datasource is empty, we need to clear the text, otherwise the last text is shown
                if (this._kendoElement.dataSource.data().length == 0) {
                    this._kendoElement.text(null);
                } else {
                    this._kendoElement.select(0);
                    if (this.valueBeforeBinding === "" || this.valueBeforeBinding != this._kendoElement.value()) {
                        this._kendoElement.trigger("change");
                    }
                }
                this.valueBeforeBinding = undefined;
            }
        }

        this.trigger(ControlEventTypes.DataBound);
    }

    private onError(e: kendo.data.DataSourceErrorEvent): void {
        console.log(e.errorThrown, e.errors);
    }

    /**
     * Set the selected value.
     * @param value Value to be set.
     * @since 4.0.0
     */
    public setValue(value: string): void {
        let oldValue = this.getValue();
        if (oldValue !== value) {
            this._kendoElement.value(value);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Get the selected value.
     * @since 4.0.0
     */
    public getValue(): string {
        let val = this._kendoElement.value();
        return val == "" ? null : val;
    }

    /**
     * Get the text of the selected item.
     * @since 4.0.0
     */
    public getText(): string {
        return this._kendoElement.text();
    }

    /**
     * Get the index of the selected item.
     * @returns Zero-based index of the selected item.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.getSelectedIndex();
     * ```
     */
    public getSelectedIndex(): number {
        return this._kendoElement.select();
    }

    /**
     * Get the kendo combobox control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/combobox|here} for the api definition.
     * @since 4.0.0
     */
    public getKendoElement(): kendo.ui.ComboBox {
        return this._kendoElement;
    }

    private oldValue: string;

    /**
     * Occurs when control value was changed.
     * @param e EventArgs.
     */
    private onChange(e: kendo.ui.ComboBoxEvent) {
        // check if the user entered a text but didnt select a value
        if (this._kendoElement.text() != "" && this._kendoElement.select() === -1) {
            setTimeout(() => {
                this._kendoElement.value(""); //reset widget
            }, 15);
        } else {
            // some constellation triggered the kendo change event even if there was no value change.
            if (this.getValue() != this.oldValue) {
                this.trigger(ControlEventTypes.Change, this);
            }
        }
        this.oldValue = this.getValue();
    }

    /**
     * Enable the control.
     * @since 4.0.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.0.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Returns true if the control is enabled.
     * @since 4.0.0
     */
    public isEnabled(): boolean {
        return !this._kendoElement.input.prop("disabled");
    }

    /**
     * Get info for requesting data.
     * @hidden
     */
    public getDataRequest(): DataRequestOption {
        if (this._kendoElement == null) {
            return null;
        }
        let result: DataRequestOption = {
            ControlID: this.controlDefinition.ControlID,
            SearchValue: this._kendoElement.value() != "" ? [this._kendoElement.value()] : [],
            Take: this.controlDefinition.LoadDataAsynchronously ? this.controlDefinition.ItemsPerRequest || 10 : null,
            Skip: null,
            FilterOperator: null,
            SearchText: null,
        };
        return result;
    }

    private setDataPromise: Promise<void>;
    private setDataPromiseResolve: any;

    /**
     * Set the odata-result to the control.
     * @param data Data to be set.
     * @hidden
     */
    public setData(data: IODataResult): Promise<void> {
        this.data = data;

        if (this.controlDefinition.LoadDataAsynchronously) {
            // the loading of the virtual combobox continues after returning above methods.
            // therefore we need to wait for the promise to be resolved in the onDataBound handler
            this.setDataPromise = new Promise((resolve, rject) => {
                this.setDataPromiseResolve = resolve;
            });

            let value = this._kendoElement.value();
            this.valueIndex = data.selectedIndex.length > 0 ? data.selectedIndex[0] : undefined;
            this._kendoElement.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
            this.dataSource = this._kendoElement.dataSource;
            this._kendoElement.value(value);

            return this.setDataPromise;
        } else {
            this._kendoElement.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
            this.dataSource = this._kendoElement.dataSource;
            this._kendoElement.dataSource.read();
            return Promise.resolve();
        }
    }

    /**
     * Show loading indicator on the control.
     * @param isBusy True if loading indicator should be shown.
     * @since 4.0.0
     */
    public setBusy(isBusy: boolean): void {
        let element = this.getOuterElement().find("button > .k-icon");
        if (isBusy) {
            element.empty();
            element.addClass("k-i-loading");
        } else {
            kendo.ui.icon(element, { icon: "caret-alt-down" });
            element.removeClass("k-i-loading");
        }
    }

    /**
     * Select the item at index.
     * @param index The zero-based index of the item which should be selected.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // in script:
     * @@ctr:1337@@.selectItemAt(5);
     *
     * // on page:
     * $dms(1337).selectItemAt(5);
     * ```
     */
    public selectItemAt(index: number): void {
        this._kendoElement.select(index);
        this._kendoElement.trigger("change");
    }

    /**
     * Returns true if an item with given value is in the dropdown list.
     * @param value The value to be searched.
     * @since 4.0.0
     * @returns True if the value is in the list of available values.
     * @example
     * ```JavaScript
     * @@ctr:1337@@.containsValue("1");
     * ```
     */
    public containsValue(value: string): boolean {
        this._kendoElement.dataSource.fetch(() => {
            let list = this._kendoElement.dataSource.data();
            let columnName = this.controlDefinition.CommandValueColumnName;
            for (let i = 0, len = list.length; i < len; i++) {
                if (list[i][columnName] == value) {
                    return true;
                }
            }
        });
        return false;
    }

    /**
     * Select the item which text contains given search string.
     * @param text The text to be searched.
     * @since 4.0.0
     */
    public selectItemWithText(text: string): void {
        if (text == null) {
            this.selectItemAt(-1);
            return;
        }
        this._kendoElement.dataSource.fetch(() => {
            let textColumnName = this.controlDefinition.CommandTextColumnName;

            this._kendoElement.select((dataItem: IDataRow) => {
                return dataItem[textColumnName] && dataItem[textColumnName].toString().indexOf(text) !== -1;
            });
            this._kendoElement.trigger("change");
        });
    }

    /**
     * Reloads the data from the datasource and selects the value after the loading.
     * This is useful when you create a entry in an additional form and want to select the entry after saving the form.
     * @param value The value to be selected after reloading.
     * @since 4.0.0
     */
    public reloadAndSetValue(value: string): Promise<void> {
        if (this.controlDefinition.LoadDataAsynchronously) {
            let val = value ? value.toString() : null;
            this._kendoElement.element.val(val);
            this._kendoElement.input.val("");
            this._kendoElement.trigger("change");
        } else {
            this._kendoElement.dataSource.data([
                { [this._kendoElement.options.dataValueField]: value, [this._kendoElement.options.dataTextField]: "" },
            ]);
            this._kendoElement.value(value);
        }
        return this.reload();
    }

    /**
     * Reload the control items with data from the server.
     * @since 4.0.0
     */
    public reload(): Promise<void> {
        if (this.controlDefinition.LoadDataAsynchronously) {
            (<any>this._kendoElement).listView._values = [""];
        }
        return this.execute<Promise<void>>(ControlEventTypes.ControlReloadRequested);
    }

    /**
     * Set the search filter for the combobox.
     * @param text Text to be searched.
     * @since 4.0.2
     */
    public setSearch(text: string): void {
        this._kendoElement.search(text);
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement) {
            this._kendoElement.destroy();
        }
        if (this.jQueryElement) {
            this.jQueryElement.off("focusout");
        }
    }
}
